import React, { useEffect, useRef, useState } from "react";
import PropTypes, { element } from "prop-types";
import { useDispatch } from "react-redux";
import * as XLSX from "xlsx-js-style";
import classes from "../../pages/Order/RegisterOrders.module.css";
import { toast } from "react-toastify";
import "../../App.css";
import DefaultInput from "./DefaultInput";
import { Button, Upload } from "antd";
import { ReactComponent as Attachment } from "../../assets/attachment-upload-alt.svg";
import { ReactComponent as ArrowUpload } from "../../assets/arrow-upload.svg";
import { ReactComponent as DownloadExcel } from "../../assets/downloadShallbon.svg";
import useLang from "../../hooks/useLang";

interface ColumnProperties {
	required: boolean;
	nullable: boolean;
}

const DefaultExcelImport = ({
	onExcelImport,
	excelHeaders,
	childClassName,
	onApiCall,
	handleLoadingChange,
	disable,
}: any) => {
	const dispatch = useDispatch();
	const inputFile = useRef<HTMLInputElement | null>(null);
	const [fileName, setFileName] = useState<string>("");
	const { strings } = useLang("defaultExcelImport");

	const [isDisable, setIsDisable] = useState(disable);

	const clickHandler = async (event: any) => {
		event.preventDefault();
	};

	const fileSelectedHandler = async (event?: any) => {
		setFileName("");
		if (event && event.target.files) {
			let fileObj = event.target.files[0];
			setFileName(fileObj.name);
		}
	};

	const ngarkoButtonHandler = async () => {
		if (!fileName) {
			toast.error(strings?.toastErrors.selectFile);
			return;
		}

		if (!fileName.endsWith(".xlsx")) {
			toast.error(strings?.toastErrors.validFile);
			return;
		}

		if (
			!inputFile.current ||
			!inputFile.current.files ||
			!inputFile.current.files[0]
		) {
			toast.error(strings?.toastErrors.fileNotFound);
			return;
		}
		setIsDisable(true);

		const fileObj = inputFile.current.files[0];

		await excelRenderer(fileObj, async (err: any, resp: any) => {
			if (err) {
				console.error(err);
				toast.error(strings?.toastErrors.fileReadError);
				handleLoadingChange(false);
				setIsDisable(false);
				return;
			}

			if (resp.rows.length === 0) {
				toast.error(strings?.toastErrors.noData);
				handleLoadingChange(false);
				setIsDisable(false);

				return;
			}

			const expectedHeaders = convertHeaders(excelHeaders)[0];

			const missingColumns = [];
			for (const key in expectedHeaders) {
				if (
					key !== "Refid" &&
					key !== "Kodi doganor" &&
					key !== "Vlera reale e porosisë" &&
					key !== "Gjerësia" &&
					key !== "Gjatësia" &&
					key !== "Lartësia" &&
					key !== "Pesha" &&
					!resp.rows[0].hasOwnProperty(key)
				) {
					missingColumns.push(key);
				}
			}

			if (missingColumns.length > 0) {
				toast.error(
					`${strings?.toastErrors
						.missingColumnsPrefix}${missingColumns.join(", ")}`
				);
				setIsDisable(false);

				return;
			}

			const emptyRows: any = [];
			resp.rows.forEach((row: any, rowIndex: any) => {
				const emptyColumns = [];
				for (const key in expectedHeaders) {
					if (
						key !== "Refid" &&
						key !== "Kodi doganor" &&
						key !== "Vlera reale e porosisë" &&
						key !== "Gjerësia" &&
						key !== "Gjatësia" &&
						key !== "Lartësia" &&
						key !== "Pesha" &&
						(!row.hasOwnProperty(key) || !row[key])
					) {
						emptyColumns.push(key);
					}
				}
				if (emptyColumns.length > 0) {
					emptyRows.push(
						`Rreshti ${rowIndex + 1}: ${emptyColumns.join(", ")}`
					);
				}
			});

			if (emptyRows.length === 0) {
				onExcelImport(resp.rows);
			} else {
				toast.error(
					`${strings?.toastErrors.emptyValuesPrefix}${emptyRows.join(
						"\n"
					)}`
				);
				setIsDisable(false);
			}
		});

		setFileName("");
		if (inputFile.current) {
			inputFile.current.value = "";
		}
	};

	const compareObjectKeys = (obj1: any, obj2: any) => {
		const keys1 = Object.keys(obj1);
		const keys2 = Object.keys(obj2);

		if (keys1.length !== keys2.length) {
			return false;
		}
		for (let i = 0; i < keys1.length; i++) {
			if (obj1[i] !== obj2[i]) {
				return false;
			}
		}
		return true;
	};

	function excelRenderer(file: any, callback: any) {
		return new Promise(function (resolve, reject) {
			var reader = new FileReader();
			var rABS = !!reader.readAsBinaryString;
			reader.onload = function (e: any) {
				/* Parse data */
				var bstr = e.target.result;
				var wb = XLSX.read(bstr, { type: rABS ? "binary" : "array" });

				/* Get first worksheet */
				var wsname = wb.SheetNames[0];
				var ws = wb.Sheets[wsname];

				/* Convert array of arrays */
				var json = XLSX.utils.sheet_to_json(ws, { raw: false });
				var cols = make_cols(ws["!ref"]);

				var data = { rows: json, cols: cols };

				resolve(data);
				return callback(null, data);
			};
			if (file && rABS) reader.readAsBinaryString(file);
			else reader.readAsArrayBuffer(file);
		});
	}

	function make_cols(refstr: any) {
		var o = [],
			C = XLSX.utils.decode_range(refstr).e.c + 1;
		for (var i = 0; i < C; ++i) {
			o[i] = { name: XLSX.utils.encode_col(i), key: i };
		}
		return o;
	}

	const convertHeaders = (excelHeaders: any) => {
		let finalObject = {};

		for (const [key, value] of Object.entries(excelHeaders)) {
			let newObject = key;

			finalObject = {
				...finalObject,
				[newObject]: "",
			};
		}
		return [finalObject];
	};

	const onClickedHandler = () => {
		// Create a new workbook
		const wb = XLSX.utils.book_new();

		// Convert headers to an array of objects with header names as values
		const headerRow = convertHeaders(excelHeaders); // [{ Header1: "Header1", Header2: "Header2", ... }]

		// Convert the header row to a worksheet
		const ws = XLSX.utils.json_to_sheet(headerRow);

		// Apply styles to the header row
		const headerKeys = Object.keys(excelHeaders);
		headerKeys.forEach((key, index) => {
			const cellAddress = XLSX.utils.encode_cell({ r: 0, c: index }); // r: row (0-based), c: column (0-based)
			if (!ws[cellAddress]) {
				ws[cellAddress] = { t: "s", v: key };
			}
			ws[cellAddress].s = {
				font: {
					name: "Calibri",
					sz: 12, // Font size
					bold: true, // Bold text
					color: { rgb: "FFFFFF" },
				},
				fill: {
					fgColor: { rgb: "6d31a2" },
				},
				alignment: {
					vertical: "center", // Vertical alignment
					horizontal: "start", // Horizontal alignment
				},
			};
		});
		ws["!rows"] = [{ hpx: 20 }];
		const wscols = headerKeys.map(() => ({ wpx: 100 })); // Example: 150 pixels width for each column
		ws["!cols"] = wscols;

		// Append the worksheet to the workbook
		XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

		// Trigger the download
		XLSX.writeFile(wb, "Sheet.xlsx");
	};

	const yesClickedHandler = () => {
		inputFile?.current?.click();
	};

	return (
		<>
			<div className={classes.uploadDiv}>
				<DefaultInput
					value={fileName}
					placeholder={strings?.placeholders.filePlaceholder}
					label={strings?.labels.uploadLabel}
					className={classes.uploadInput}
					allowClear
					suffix={
						<div>
							<Attachment
								onClick={yesClickedHandler}
								className="cursor-pointer"
							/>
						</div>
					}
				/>
				<Button
					className={classes.button}
					onClick={ngarkoButtonHandler}
					disabled={isDisable}
				>
					<ArrowUpload /> {strings?.buttonText}
				</Button>
				<input
					type="file"
					id="file"
					ref={inputFile}
					style={{ display: "none" }}
					onChange={fileSelectedHandler}
				/>
				<p onClick={onClickedHandler} className={classes.shabllon}>
					<DownloadExcel /> {strings?.shabllonText}
				</p>
			</div>
		</>
	);
};

DefaultExcelImport.propTypes = {
	onExcelImport: PropTypes.func,
	toggleModal: PropTypes.func,
	excelHeaders: PropTypes.object.isRequired,
	childClassName: PropTypes.string,
	disable: PropTypes.bool,
};

export default DefaultExcelImport;
